import { supabase } from './supabase';

export interface CollectionMember {
  userId: string;
  displayName: string;
  email: string;
  isOwner: boolean;
  joinedAt: string;
}

interface DBCollectionMemberRow {
  user_id: string;
  display_name: string;
  email: string;
  is_owner: boolean;
  joined_at: string;
}

function mapMember(row: DBCollectionMemberRow): CollectionMember {
  return {
    userId: row.user_id,
    displayName: row.display_name,
    email: row.email,
    isOwner: row.is_owner,
    joinedAt: row.joined_at,
  };
}

export async function fetchCollectionMembers(
  collectionId: string
): Promise<CollectionMember[]> {
  const { data, error } = await supabase.rpc('list_collection_members', {
    p_collection_id: collectionId,
  });

  if (error) throw error;
  return ((data ?? []) as DBCollectionMemberRow[]).map(mapMember);
}

export async function inviteCollectionMemberByEmail(
  collectionId: string,
  email: string
): Promise<void> {
  const { error } = await supabase.rpc('invite_collection_member_by_email', {
    p_collection_id: collectionId,
    p_email: email.trim(),
  });

  if (error) throw error;
}

export async function inviteCollectionMembersByEmail(
  collectionId: string,
  emails: string[]
): Promise<{ invited: string[]; failed: { email: string; message: string }[] }> {
  const invited: string[] = [];
  const failed: { email: string; message: string }[] = [];

  for (const email of emails) {
    const trimmed = email.trim();
    if (!trimmed) continue;
    try {
      await inviteCollectionMemberByEmail(collectionId, trimmed);
      invited.push(trimmed);
    } catch (err) {
      failed.push({
        email: trimmed,
        message: err instanceof Error ? err.message : 'Failed to invite',
      });
    }
  }

  return { invited, failed };
}

export async function removeCollectionMember(
  collectionId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase.rpc('remove_collection_member', {
    p_collection_id: collectionId,
    p_user_id: userId,
  });

  if (error) throw error;
}

export function parseEmailList(input: string): string[] {
  const emails = input
    .split(/[,;\s]+/)
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(emails));
}
